import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, RotateCw, Wand2, Eraser, ShieldCheck, ImageOff } from "lucide-react";

// Card Artwork Processing Studio — upload a real card photo → detect → crop →
// perspective-correct → mask sensitive data → preview → save. Sensitive
// originals are handled server-side (restricted + purged after save).
export default function CardArtworkStudio({ cardId, onClose, onSaved }) {
  const [step, setStep] = useState("upload"); // upload | edit
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);       // {job_id, cards:[{index,preview,fallback}]}
  const [idx, setIdx] = useState(0);
  const [rotate, setRotate] = useState(0);
  const [autoMask, setAutoMask] = useState(true);
  const [keepLast4, setKeepLast4] = useState(true);
  const [keepName, setKeepName] = useState(false);
  const [masks, setMasks] = useState([]);
  const [preview, setPreview] = useState(null);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const [draft, setDraft] = useState(null);

  const detect = async (file) => {
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Image must be PNG, JPG or WebP"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/card-control/cards/${cardId}/artwork/detect`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setJob(data); setIdx(0); setMasks([]); setRotate(0); setConfirmReview(false);
      setStep("edit");
      toast.success(`Detected ${data.detected} card${data.detected === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Detection failed"); }
    setBusy(false);
  };

  const runProcess = useCallback(async () => {
    if (!job) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/card-control/cards/${cardId}/artwork/process`, {
        job_id: job.job_id, card_index: idx, rotate, masks,
        auto_mask: autoMask, keep_last4: keepLast4, keep_name: keepName,
      });
      setPreview(data.preview); setReviewRequired(data.review_required);
    } catch (e) { toast.error(e.response?.data?.detail || "Processing failed"); }
    setBusy(false);
  }, [job, cardId, idx, rotate, masks, autoMask, keepLast4, keepName]);

  useEffect(() => { if (step === "edit" && job) runProcess(); }, [step, job, idx, rotate, masks, autoMask, keepLast4, keepName, runProcess]);

  const selectedOriginal = job?.cards?.find((c) => c.index === idx)?.preview;

  // manual mask drawing (normalised rect over the preview box)
  const rel = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
             y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const onDown = (e) => { drag.current = rel(e); setDraft({ ...drag.current, w: 0, h: 0 }); };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = rel(e);
    setDraft({ x: Math.min(drag.current.x, p.x), y: Math.min(drag.current.y, p.y),
               w: Math.abs(p.x - drag.current.x), h: Math.abs(p.y - drag.current.y) });
  };
  const onUp = () => {
    if (draft && draft.w > 0.03 && draft.h > 0.02) setMasks((m) => [...m, { ...draft, kind: "manual" }]);
    drag.current = null; setDraft(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/card-control/cards/${cardId}/artwork/save`, {
        job_id: job.job_id, card_index: idx, rotate, masks,
        auto_mask: autoMask, keep_last4: keepLast4, keep_name: keepName, confirm: confirmReview,
      });
      toast.success(`Artwork saved · ${data.artwork_meta?.artwork_id || ""}`);
      onSaved?.(data); onClose?.();
    } catch (e) {
      const d = e.response?.data?._rawDetail ?? e.response?.data?.detail;
      if (e.response?.status === 409 && (d?.review_required)) {
        setReviewRequired(true);
        toast.warning("Review required — confirm before saving");
      } else { toast.error((typeof d === "object" ? d.message : d) || "Save failed"); }
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="card-artwork-studio">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="h-4 w-4 text-indigo-600" /> Card Artwork Studio</DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-3">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-1">Upload a clear photo of the physical card</p>
              <p className="text-[11px] text-slate-400 mb-3">We detect the card, straighten it, remove the background and mask the number/name automatically. Sensitive originals are never stored publicly and are purged after save.</p>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                onChange={(e) => e.target.files?.[0] && detect(e.target.files[0])} data-testid="artwork-file" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="artwork-upload-btn"
                className="bg-indigo-700 hover:bg-indigo-800 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Choose photo
              </Button>
            </div>
          </div>
        )}

        {step === "edit" && job && (
          <div className="space-y-3">
            {job.detected > 1 && (
              <div data-testid="artwork-multi">
                <div className="text-[11px] font-semibold text-slate-500 mb-1">Detected {job.detected} cards — pick the one to process</div>
                <div className="flex gap-2 flex-wrap">
                  {job.cards.map((c) => (
                    <button key={c.index} onClick={() => setIdx(c.index)} data-testid={`artwork-card-${c.index}`}
                      className={`rounded-lg overflow-hidden border-2 ${idx === c.index ? "border-indigo-500" : "border-transparent"}`}>
                      <img src={c.preview} alt="" className="h-16 w-auto object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Original</div>
                {selectedOriginal
                  ? <img src={selectedOriginal} alt="original" className="w-full rounded-lg border border-slate-200" data-testid="artwork-original" />
                  : <div className="aspect-[1.6/1] rounded-lg bg-slate-100" />}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Processed artwork · drag to mask</div>
                <div ref={boxRef} className="relative w-full rounded-lg overflow-hidden border border-slate-200 select-none cursor-crosshair"
                  onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} data-testid="artwork-processed-box">
                  {preview
                    ? <img src={preview} alt="processed" className="w-full block pointer-events-none" data-testid="artwork-processed" />
                    : <div className="aspect-[1.6/1] bg-slate-100 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
                  {draft && <div className="absolute bg-black/40 border border-white/70" style={{
                    left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.w * 100}%`, height: `${draft.h * 100}%` }} />}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setRotate((r) => (r + 90) % 360)} data-testid="artwork-rotate"><RotateCw className="h-3.5 w-3.5 mr-1" />Rotate</Button>
              <label className="inline-flex items-center gap-1 text-[12px]"><input type="checkbox" checked={autoMask} onChange={(e) => setAutoMask(e.target.checked)} data-testid="artwork-automask" /> Auto-protect</label>
              <label className="inline-flex items-center gap-1 text-[12px]"><input type="checkbox" checked={keepLast4} onChange={(e) => setKeepLast4(e.target.checked)} data-testid="artwork-keeplast4" /> Keep last 4</label>
              <label className="inline-flex items-center gap-1 text-[12px]"><input type="checkbox" checked={keepName} onChange={(e) => setKeepName(e.target.checked)} data-testid="artwork-keepname" /> Keep name</label>
              {masks.length > 0 && (
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setMasks([])} data-testid="artwork-clear-masks">
                  <Eraser className="h-3.5 w-3.5 mr-1" />Clear {masks.length} manual mask{masks.length === 1 ? "" : "s"}
                </Button>
              )}
              <button className="text-[11px] text-slate-500 underline decoration-dotted ml-auto" onClick={() => { setStep("upload"); setPreview(null); setJob(null); }} data-testid="artwork-reupload">
                <ImageOff className="h-3.5 w-3.5 inline mr-1" />Use a different photo
              </button>
            </div>

            {reviewRequired && (
              <label className="flex items-start gap-2 text-[12px] bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="artwork-review">
                <input type="checkbox" checked={confirmReview} onChange={(e) => setConfirmReview(e.target.checked)} data-testid="artwork-confirm" className="mt-0.5" />
                <span><b>Review required.</b> No sensitive-data mask is applied. Confirm the number/expiry are not visible before saving.</span>
              </label>
            )}
            <div className="text-[11px] text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> The full card number, expiry and CVV are never stored in the artwork; only approved last-4 may remain.</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {step === "edit" && (
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save}
              disabled={busy || !preview || (reviewRequired && !confirmReview)} data-testid="artwork-save-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save artwork
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
